import { useContext, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { HubContext } from '../../utils';

export enum HUB_FEATURES {
	BREADCRUMBS = 'BREADCRUMBS',
	CONFIGURABLE_SITE_TREE = 'CONFIGURABLE_SITE_TREE',
	DRAFT_MODE = 'DRAFT_MODE',
}

const HUB_FEATURE_CONFIG: Record<
	HUB_FEATURES,
	{ contextKey: string; isPlatformEnabled: () => boolean }
> = {
	[HUB_FEATURES.BREADCRUMBS]: {
		contextKey: 'enable_hub_breadcrumbs',
		isPlatformEnabled: () => fg('enable_hub_breadcrumbs'),
	},
	[HUB_FEATURES.CONFIGURABLE_SITE_TREE]: {
		contextKey: 'help_hubs_configurable_site_tree',
		isPlatformEnabled: () => fg('help_hubs_configurable_site_tree'),
	},
	[HUB_FEATURES.DRAFT_MODE]: {
		contextKey: 'help_hubs_draft_mode',
		isPlatformEnabled: () => fg('help_hubs_draft_mode'),
	},
};

/**
 * Returns whether each requested hub feature is enabled, in the same order as
 * `features`. A feature is enabled only when both the HubContext product flag
 * and the platform feature gate are on.
 *
 * Prefer destructuring for React deps; return identity is stable when values
 * are unchanged.
 */
export function useIsHubFeatureEnabled(features: readonly HUB_FEATURES[]): boolean[] {
	const ctx = useContext(HubContext);

	const next = features.map((feature) => {
		const { contextKey, isPlatformEnabled } = HUB_FEATURE_CONFIG[feature];
		const isContextEnabled = ctx?.featureFlags?.[contextKey] === true;
		return isContextEnabled && isPlatformEnabled();
	});

	// Reuse the previous array when values match so deps on the return value
	// do not re-fire every render.
	const cachedRef = useRef(next);
	const cached = cachedRef.current;
	if (cached.length !== next.length || next.some((value, index) => value !== cached[index])) {
		cachedRef.current = next;
	}

	return cachedRef.current;
}
